"use client";

import {
  Alert,
  AlertDescription,
} from "@nyntax/design-system/components/ui/alert";
import { GoogleIcon } from "@nyntax/design-system/components/logo/google";
import { Button } from "@nyntax/design-system/components/ui/button";
import { useState } from "react";

import { startGoogleSignIn } from "@/lib/auth";

/** Translated strings the Google button shows. */
export interface GoogleButtonLabels {
  failed: string;
  label: string;
  or: string;
}

interface GoogleButtonProps {
  /** A same-site path (already checked) for someone who already has an account. */
  callbackURL: string;
  /** A same-site path to come back to if Google or the sign-in fails; it shows `labels.failed`. */
  errorCallbackURL: string;
  /** Show the failure message at once, for when the person is back from a failed Google round trip. */
  failedOnLoad?: boolean;
  labels: GoogleButtonLabels;
  /** A same-site path for an account this sign-in creates. */
  newUserCallbackURL?: string;
}

/**
 * "Continue with Google" with an "or" divider under it, for the top of the
 * sign-in and sign-up forms. It asks Better Auth for the Google address and
 * sends the browser there; the account is the shared ClassTablet one.
 *
 * @param props - The return addresses and the words.
 * @returns The button and divider.
 */
export const GoogleButton = ({
  callbackURL,
  errorCallbackURL,
  failedOnLoad = false,
  labels,
  newUserCallbackURL,
}: GoogleButtonProps) => {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(failedOnLoad);

  const go = async () => {
    setFailed(false);
    setBusy(true);

    const url = await startGoogleSignIn({
      callbackURL,
      errorCallbackURL,
      newUserCallbackURL,
    });

    if (url === null) {
      setBusy(false);
      setFailed(true);

      return;
    }

    window.location.assign(url);
  };

  return (
    <div className="space-y-4">
      <Button
        className="w-full"
        disabled={busy}
        onClick={go}
        size="xl"
        type="button"
        variant="outline"
      >
        <GoogleIcon aria-hidden="true" className="size-5" />
        {labels.label}
      </Button>
      {failed ? (
        <Alert variant="error">
          <AlertDescription>{labels.failed}</AlertDescription>
        </Alert>
      ) : null}
      <div className="text-muted-foreground flex items-center gap-3 text-xs uppercase">
        <span className="bg-border h-px flex-1" />
        {labels.or}
        <span className="bg-border h-px flex-1" />
      </div>
    </div>
  );
};
